import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { Button, Flex, HStack, Text, VStack } from 'govwell-ui'

import QuestionCard from './QuestionCard'
import { TopQuestion } from './types'

const QUESTIONS_PER_PAGE = 3

type Props = {
  topQuestions: TopQuestion[]
}

const NotableQuestionsSection = ({ topQuestions }: Props) => {
  const [page, setPage] = useState(0)

  useEffect(() => {
    setPage(0)
  }, [topQuestions.length])

  const totalPages = useMemo(
    () => Math.ceil(topQuestions.length / QUESTIONS_PER_PAGE),
    [topQuestions.length]
  )

  const displayedQuestions = useMemo(
    () => topQuestions.slice(page * QUESTIONS_PER_PAGE, (page + 1) * QUESTIONS_PER_PAGE),
    [topQuestions, page]
  )

  const handlePrev = useCallback(() => {
    setPage((p) => Math.max(0, p - 1))
  }, [])

  const handleNext = useCallback(() => {
    setPage((p) => Math.min(totalPages - 1, p + 1))
  }, [totalPages])

  if (topQuestions.length === 0) {
    return null
  }

  return (
    <VStack gap={4}>
      <Flex direction="row" justify="space-between" align="center">
        <VStack gap={1} width="auto">
          <Text.H3 className="m-0">Notable Questions</Text.H3>
          <Text.Body className="whitespace-nowrap">
            A sample of real questions answered by your assistant
          </Text.Body>
        </VStack>
        {totalPages > 1 && (
          <HStack gap={2} align="center" width="auto">
            <Text.BodySmall className="text-gray-500">
              {page + 1} / {totalPages}
            </Text.BodySmall>
            <Button
              ariaLabel="Previous page"
              icon="FaChevronLeft"
              variant="default"
              size="md"
              isDisabled={page === 0}
              onClick={handlePrev}
            />
            <Button
              ariaLabel="Next page"
              icon="FaChevronRight"
              variant="default"
              size="md"
              isDisabled={page === totalPages - 1}
              onClick={handleNext}
            />
          </HStack>
        )}
      </Flex>
      <Flex direction="row" gap={4}>
        {displayedQuestions.map((topQuestion) => (
          <QuestionCard key={topQuestion.id} topQuestion={topQuestion} />
        ))}
      </Flex>
    </VStack>
  )
}

export default React.memo(NotableQuestionsSection)
